import React, { useState, useEffect } from 'react';
import { usePosts } from '../hooks/usePosts';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useModerator } from '../hooks/useModerator';
import { useNavigate, Link } from 'react-router-dom';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import { Textarea } from '../components/common/Textarea';
import { moderationApi } from '../api/moderation';

const DeletedPostsPageContent: React.FC = () => {
  const { posts, loading, error, fetchPosts } = usePosts();
  const isModerator = useModerator();
  const navigate = useNavigate();
  const [currentPage, _setCurrentPage] = useState(0);
  const [permanentDeletePostId, setPermanentDeletePostId] = useState<string | null>(null);
  const [restorePostId, setRestorePostId] = useState<string | null>(null);
  const [restoreReason, setRestoreReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    if (!isModerator) {
      navigate('/');
      return;
    }

    fetchPosts({ page: currentPage, size: 50, includeDeleted: true, deletedOnly: true });
  }, [currentPage, fetchPosts, isModerator, navigate]);

  // Show only soft-deleted or approved-flag posts (backend may return only these when deletedOnly=true; filter ensures we never show all posts)
  const deletedPosts = posts.filter(
    (post) => post.deleted === true || post.flagStatus === 'APPROVED'
  );

  const handlePermanentDelete = async (postId: string) => {
    setPermanentDeletePostId(postId);
    setDeleteError(null);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeletePostId) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await moderationApi.permanentlyDeletePost(permanentDeletePostId);
      // Refresh the list
      await fetchPosts({ page: currentPage, size: 50, includeDeleted: true, deletedOnly: true });
      setPermanentDeletePostId(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to permanently delete post');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (postId: string) => {
    setRestorePostId(postId);
    setRestoreReason('');
    setRestoreError(null);
  };

  const confirmRestore = async () => {
    if (!restorePostId || !restoreReason.trim()) {
      setRestoreError('Reason is required');
      return;
    }

    setRestoring(true);
    setRestoreError(null);

    try {
      await moderationApi.restorePost(restorePostId, {
        reason: restoreReason.trim(),
      });
      // Refresh the list
      await fetchPosts({ page: currentPage, size: 50, includeDeleted: true, deletedOnly: true });
      setRestorePostId(null);
      setRestoreReason('');
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Failed to restore post');
    } finally {
      setRestoring(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 150): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (!isModerator) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="deleted-posts-page">
      <div className="deleted-posts-page-header">
        <h1>Deleted Posts</h1>
        <p className="deleted-posts-description">
          View all soft-deleted posts. Only visible to moderators.
        </p>
      </div>

      {deletedPosts.length === 0 ? (
        <div className="deleted-posts-empty">
          <p>No deleted posts found.</p>
        </div>
      ) : (
        <div className="deleted-posts-table-container">
          <table className="deleted-posts-table">
            <thead>
              <tr>
                <th>Deletion Reason</th>
                <th>Post Title & Content</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deletedPosts.map((post) => (
                <tr key={post.id}>
                  <td className="deletion-reason-cell">
                    {post.deletionReason || <em>No reason provided</em>}
                  </td>
                  <td className="post-content-cell">
                    <Link to={`/posts/${post.id}?includeDeleted=true`} className="post-link">
                      {post.title && (
                        <div className="post-title-truncated">
                          <strong>{truncateText(post.title, 80)}</strong>
                        </div>
                      )}
                      <div className="post-content-truncated">
                        {truncateText(post.content, 150)}
                      </div>
                    </Link>
                  </td>
                  <td className="actions-cell">
                    <div className="deleted-posts-actions">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleRestore(post.id)}
                      >
                        Restore
                      </Button>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={() => handlePermanentDelete(post.id)}
                      >
                        Delete Permanently
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={permanentDeletePostId !== null}
        onClose={() => {
          setPermanentDeletePostId(null);
          setDeleteError(null);
        }}
        title="Permanently Delete Post"
      >
        <div className="permanent-delete-modal">
          <p>
            <strong>Warning:</strong> This action cannot be undone. The post will be permanently removed from the database.
          </p>
          <p>Are you sure you want to permanently delete this post?</p>
          {deleteError && <div className="error-message">{deleteError}</div>}
          <div className="modal-actions">
            <Button
              variant="danger"
              onClick={confirmPermanentDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setPermanentDeletePostId(null);
                setDeleteError(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={restorePostId !== null}
        onClose={() => {
          setRestorePostId(null);
          setRestoreReason('');
          setRestoreError(null);
        }}
        title="Restore Post"
      >
        <div className="restore-modal">
          <p>Are you sure you want to restore this post?</p>
          <Textarea
            label="Reason for restoration"
            value={restoreReason}
            onChange={(e) => setRestoreReason(e.target.value)}
            placeholder="Enter reason for restoration..."
            required
            rows={4}
          />
          {restoreError && <div className="error-message">{restoreError}</div>}
          <div className="modal-actions">
            <Button
              variant="primary"
              onClick={confirmRestore}
              disabled={restoring || !restoreReason.trim()}
            >
              {restoring ? 'Restoring...' : 'Restore'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setRestorePostId(null);
                setRestoreReason('');
                setRestoreError(null);
              }}
              disabled={restoring}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const DeletedPostsPage: React.FC = () => {
  return (
    <ProtectedRoute>
      <DeletedPostsPageContent />
    </ProtectedRoute>
  );
};
